import { Alert, Button, Snackbar } from '@mui/material'
import { ContractReceipt } from 'ethers'
import { createContext, ReactNode, useContext, useState } from 'react'
import { errorHandler } from 'src/utils/commons'
import { useWeb3Context } from './web3Context'

export interface ITxStatusData {
  txHash: string
  status: 'success' | 'info' | 'error'
  message: string
}

export interface ITxStatusContext {
  handleTx: (method: any, params: any, callback?: any, onError?: any) => Promise<ContractReceipt>
  renderTxStatusAlert: () => ReactNode
}

export const TX_STATUS_CONTEXT_DEFAULT_VALUE = {
  handleTx: undefined,
  renderTxStatusAlert: undefined
}

const TxStatusContext = createContext<ITxStatusContext>(TX_STATUS_CONTEXT_DEFAULT_VALUE)

export const TxStatusContextProvider = ({ ...props }) => {
  const [txStatusOpen, setTxStatusOpen] = useState<boolean>(false)
  const [txStatusData, setTxStatusData] = useState<ITxStatusData>()

  const openTxStatusAlert = (txHash, status: 'success' | 'info' | 'error', message: string) => {
    setTxStatusData({ txHash, status, message })
    setTxStatusOpen(true)
  }

  const closeTxStatusAlert = () => {
    setTxStatusOpen(false)
    setTxStatusData(undefined)
  }

  const handleTx = async (method: any, params: any, callback?: any, onError?: any): Promise<ContractReceipt> => {
    try {
      const tx = await method(...params)
      openTxStatusAlert(tx.hash, 'info', 'Transactions is pending!')
      const receipt = await tx.wait()
      openTxStatusAlert(receipt.transactionHash, 'success', 'Transaction completed')
      callback?.(receipt);
      return receipt
    } catch (err) {
      errorHandler(err)
      openTxStatusAlert(err.transactionHash, 'error', 'Transaction errored')
      onError?.(err)
    }
  }

  const renderTxStatusAlert = (): ReactNode => {
    const { chainData } = useWeb3Context()
    return (
      <Snackbar
        open={txStatusOpen}
        onClose={closeTxStatusAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {txStatusData?.status && (
          <Alert
            onClose={closeTxStatusAlert}
            severity={txStatusData.status}
            sx={{ width: '100%' }}
            action={ txStatusData?.txHash ?
              <Button variant='outlined' color={txStatusData.status} size="small" onClick={() => {
                window.open(`${chainData.bscscan_explorer_url}/tx/${txStatusData.txHash}`, "_blank")
                closeTxStatusAlert()
              }}>
                View on explorer
              </Button>
              : undefined
            }
          >
            {txStatusData.message}
          </Alert>
        )}
      </Snackbar>
    )
  }

  const statusObj = {
    handleTx,
    renderTxStatusAlert
  }

  return <TxStatusContext.Provider value={statusObj}>{props.children}</TxStatusContext.Provider>
}

export const useTxStatusContext = (): ITxStatusContext => {
  const context = useContext(TxStatusContext)
  if (!context) {
    throw new Error('[useTxStatusContext] Hook not used under tx status context provider')
  }
  return context
}
